import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  register,
  clearError,
  clearPendingVerificationEmail,
} from "../../store/slices/authSlice";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import logo from "../../assets/logo.png";
import { COUNTRIES } from "../../utils/countries";

interface RegisterForm {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  password_confirmation: string;
  agreeToTerms: boolean;
  referral_code?: string;
  country: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error, pendingVerificationEmail } = useSelector(
    (state: RootState) => state.auth,
  );

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [referralCode, setReferralCode] = useState<string>("");

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refCode = searchParams.get("ref");
    if (refCode) {
      setReferralCode(refCode);
    }
  }, [location]);

  const {
    register: formRegister,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>({
    defaultValues: {
      referral_code: referralCode,
    },
  });

  const password = watch("password");
  const siteName = process.env.REACT_APP_SITE_NAME || "XRP Trading Platform";

  useEffect(() => {
    if (pendingVerificationEmail) {
      navigate("/verify-email", {
        state: {
          email: pendingVerificationEmail,
          fromRegistration: true,
        },
      });
      dispatch(clearPendingVerificationEmail());
    }
  }, [pendingVerificationEmail, navigate, dispatch]);

  useEffect(() => {
    if (registrationSuccess && registeredEmail) {
      const timer = setTimeout(() => {
        navigate("/verify-email", {
          state: {
            email: registeredEmail,
            fromRegistration: true,
          },
        });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [registrationSuccess, registeredEmail, navigate]);

  const onSubmit = async (data: RegisterForm) => {
    try {
      const registrationData = {
        ...data,
        referral_code: referralCode || data.referral_code,
      };

      const result = await dispatch(register(registrationData));

      if (register.fulfilled.match(result)) {
        setRegistrationSuccess(true);
        setRegisteredEmail(data.email);
        dispatch(clearError());
      }
    } catch (error) {
      console.error("Registration error:", error);
    }
  };

  if (registrationSuccess) {
    return (
      <div className={globalStyle.container}>
        <main className="page-content">
          <div className="max-w-md mx-auto py-8 md:py-12 gap-10">
            <Link to="/" className="text-center mb-8">
              <img
                src={logo}
                alt={siteName}
                className="w-16 h-16 mx-auto mb-4"
              />
              <h2 className={globalStyle.h3}>Registration Successful!</h2>
              <p className={globalStyle.body2 + " text-gray-400"}>
                Your account has been created successfully
              </p>
            </Link>

            <Card padding="lg" className="mt-6">
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-900 flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-green-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className={globalStyle.h5}>Check Your Email</h3>
                <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                  We've sent a verification code to:
                  <br />
                  <strong className="text-white">{registeredEmail}</strong>
                </p>
                {referralCode && (
                  <div className="mt-3 p-3 bg-blue-900/30 rounded-lg">
                    <p className="text-sm text-blue-300">
                      ✅ You've joined via referral link! You may be eligible
                      for bonuses.
                    </p>
                  </div>
                )}
                <p className={`${globalStyle.caption} mt-4 text-gray-500`}>
                  Please check your inbox (and spam folder) for the 6-digit OTP
                </p>
                <div className="mt-6 space-y-4">
                  <Button
                    onClick={() =>
                      navigate("/verify-email", {
                        state: {
                          email: registeredEmail,
                          fromRegistration: true,
                        },
                      })
                    }
                    fullWidth
                    size="lg"
                  >
                    Go to Email Verification
                  </Button>
                  <Link to="/login">
                    <Button variant="tertiary" fullWidth>
                      Back to Login
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={globalStyle.container}>
      <main className="page-content2">
        <div className="page-content2-bg"></div>
        <div className="page-content2-wrapper">
          <div className="page-content2-content">
            <div className="max-w-md mx-auto py-8 md:py-12">
              <div className="text-center mb-8">
                <img
                  src={logo}
                  alt={siteName}
                  className="w-16 h-16 mx-auto mb-4"
                />
                <h2 className={globalStyle.h3}>Create Account</h2>
                <p className={globalStyle.body2 + " text-gray-400"}>
                  Join {siteName} and start your investment journey
                </p>

                {referralCode && (
                  <div className="mt-2 p-3 bg-blue-900/30 rounded-lg">
                    <p className="text-sm text-blue-300">
                      🎉 You're joining via a referral link! The referrer may
                      earn bonuses from your activity.
                    </p>
                    <p className="text-xs text-blue-200 mt-1">
                      Referral Code:{" "}
                      <code className="font-mono">{referralCode}</code>
                    </p>
                  </div>
                )}
              </div>

              <Card padding="lg">
                {error && (
                  <Alert
                    type="error"
                    message={error}
                    onClose={() => dispatch(clearError())}
                    className="mb-6"
                  />
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* First / Last Name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>First Name</label>
                      <input
                        type="text"
                        className={globalStyle.input}
                        placeholder="John"
                        {...formRegister("first_name", {
                          required: "First name is required",
                          minLength: { value: 2, message: "Min 2 characters" },
                          maxLength: {
                            value: 50,
                            message: "Max 50 characters",
                          },
                        })}
                      />
                      {errors.first_name && (
                        <p className={globalStyle.formError}>
                          {errors.first_name.message}
                        </p>
                      )}
                    </div>

                    <div className={globalStyle.formGroup}>
                      <label className={globalStyle.label}>Last Name</label>
                      <input
                        type="text"
                        className={globalStyle.input}
                        placeholder="Doe"
                        {...formRegister("last_name", {
                          required: "Last name is required",
                          minLength: { value: 2, message: "Min 2 characters" },
                          maxLength: {
                            value: 50,
                            message: "Max 50 characters",
                          },
                        })}
                      />
                      {errors.last_name && (
                        <p className={globalStyle.formError}>
                          {errors.last_name.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Username */}
                  <div className={globalStyle.formGroup}>
                    <label className={globalStyle.label}>Username</label>
                    <input
                      type="text"
                      className={globalStyle.input}
                      placeholder="johndoe"
                      {...formRegister("username", {
                        required: "Username is required",
                        minLength: { value: 3, message: "Min 3 characters" },
                        maxLength: { value: 50, message: "Max 50 characters" },
                        pattern: {
                          value: /^[a-zA-Z0-9_]+$/,
                          message: "Letters, numbers and underscores only",
                        },
                      })}
                    />
                    {errors.username && (
                      <p className={globalStyle.formError}>
                        {errors.username.message}
                      </p>
                    )}
                    <p className={`${globalStyle.caption} mt-1 text-gray-500`}>
                      Only letters, numbers, and underscores allowed
                    </p>
                  </div>

                  {/* Email */}
                  <div className={globalStyle.formGroup}>
                    <label className={globalStyle.label}>Email Address</label>
                    <input
                      type="email"
                      className={globalStyle.input}
                      placeholder="john@example.com"
                      {...formRegister("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address",
                        },
                      })}
                    />
                    {errors.email && (
                      <p className={globalStyle.formError}>
                        {errors.email.message}
                      </p>
                    )}
                    <p className={`${globalStyle.caption} mt-1 text-gray-500`}>
                      We'll send a verification code to this email
                    </p>
                  </div>

                  {/* Country */}
                  <div className={globalStyle.formGroup}>
                    <label className={globalStyle.label}>Country</label>
                    <select
                      className={globalStyle.input}
                      {...formRegister("country", {
                        required: "Please select your country",
                      })}
                    >
                      <option value="">— Select your country —</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.flag} {c.name}
                        </option>
                      ))}
                    </select>
                    {errors.country && (
                      <p className={globalStyle.formError}>
                        {errors.country.message}
                      </p>
                    )}
                  </div>

                  {/* Password */}
                  <div className={globalStyle.formGroup}>
                    <label className={globalStyle.label}>Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className={globalStyle.input}
                        placeholder="Create a strong password"
                        {...formRegister("password", {
                          required: "Password is required",
                          minLength: { value: 8, message: "Min 8 characters" },
                          validate: {
                            hasUpperCase: (v) =>
                              /[A-Z]/.test(v) ||
                              "Must contain an uppercase letter",
                            hasLowerCase: (v) =>
                              /[a-z]/.test(v) ||
                              "Must contain a lowercase letter",
                            hasNumber: (v) =>
                              /[0-9]/.test(v) || "Must contain a number",
                            hasSpecialChar: (v) =>
                              /[!@#$%^&*(),.?":{}|<>]/.test(v) ||
                              "Must contain a special character",
                          },
                        })}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                    {errors.password && (
                      <p className={globalStyle.formError}>
                        {errors.password.message}
                      </p>
                    )}
                    <div className="mt-2">
                      <p
                        className={`${globalStyle.caption} mb-1 text-gray-500`}
                      >
                        Password must contain:
                      </p>
                      <ul className="text-xs text-gray-400 space-y-1">
                        {[
                          {
                            test: (password?.length ?? 0) >= 8,
                            label: "At least 8 characters",
                          },
                          {
                            test: /[A-Z]/.test(password || ""),
                            label: "One uppercase letter",
                          },
                          {
                            test: /[a-z]/.test(password || ""),
                            label: "One lowercase letter",
                          },
                          {
                            test: /[0-9]/.test(password || ""),
                            label: "One number",
                          },
                          {
                            test: /[!@#$%^&*(),.?":{}|<>]/.test(password || ""),
                            label: "One special character",
                          },
                        ].map(({ test, label }) => (
                          <li key={label} className="flex items-center">
                            <span
                              className={`inline-block w-2 h-2 rounded-full mr-2 ${test ? "bg-green-500" : "bg-gray-600"}`}
                            />
                            {label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className={globalStyle.formGroup}>
                    <label className={globalStyle.label}>
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className={globalStyle.input}
                        placeholder="Confirm your password"
                        {...formRegister("password_confirmation", {
                          required: "Please confirm your password",
                          validate: (value) =>
                            value === password || "Passwords do not match",
                        })}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                    {errors.password_confirmation && (
                      <p className={globalStyle.formError}>
                        {errors.password_confirmation.message}
                      </p>
                    )}
                  </div>

                  {/* Hidden referral code */}
                  <input
                    type="hidden"
                    {...formRegister("referral_code")}
                    value={referralCode}
                  />

                  {/* Terms */}
                  <div className={globalStyle.formGroup}>
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="agreeToTerms"
                        className="w-4 h-4 mt-1 text-primary border-gray-600 rounded focus:ring-primary bg-gray-800"
                        {...formRegister("agreeToTerms", {
                          required: "You must agree to the terms",
                        })}
                      />
                      <label
                        htmlFor="agreeToTerms"
                        className="ml-2 text-sm text-gray-400"
                      >
                        I agree to the{" "}
                        <Link
                          to="/terms"
                          className="text-primary hover:text-primary-300 font-medium"
                          target="_blank"
                        >
                          Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link
                          to="/privacy"
                          className="text-primary hover:text-primary-300 font-medium"
                          target="_blank"
                        >
                          Privacy Policy
                        </Link>
                      </label>
                    </div>
                    {errors.agreeToTerms && (
                      <p className={globalStyle.formError}>
                        {errors.agreeToTerms.message}
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    fullWidth
                    loading={isLoading}
                    size="lg"
                    disabled={isLoading}
                  >
                    {isLoading ? "Creating Account..." : "Create Account"}
                  </Button>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-800">
                  <p className="text-center text-sm text-gray-400">
                    Already have an account?{" "}
                    <Link
                      to="/login"
                      className="text-primary font-medium hover:text-primary-300"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </Card>

              <div className="mt-8 text-center">
                <p className={globalStyle.caption + " text-gray-500"}>
                  By creating an account, you agree to our Terms of Service and
                  Privacy Policy
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Register;
